import { Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { useEffect, useRef } from 'react';
import { DefaultLayout } from '@/components/layouts/DefaultLayout';
import { AdminLayout } from '@/components/layouts/AdminLayout';
import { AuthGuard } from '@/components/layouts/AuthGuard';
import { RequireAdmin } from '@/components/layouts/RequireAdmin';
import { ErrorBoundary } from '@/components/layouts/ErrorBoundary';
import { LoginModal } from '@/components/organisms/LoginModal';
import { ConfirmModal } from '@/components/molecules/ConfirmModal';
import { Toaster } from '@/components/organisms/Toaster';
import { HomePage } from '@/pages/HomePage';
import { BoardPage } from '@/pages/BoardPage';
import { PopularBoardPage } from '@/pages/PopularBoardPage';
import { PostDetailPage } from '@/pages/PostDetailPage';
import { WritePage } from '@/pages/WritePage';
import { MyPage } from '@/pages/MyPage';
import { AuthCallbackPage } from '@/pages/AuthCallbackPage';
import { SearchPage } from '@/pages/SearchPage';
// [법인 전환 후 활성화] 실시간 시세 페이지 — 현재 /market 은 시황 리포트 페이지로 대체
// import { MarketPage } from '@/pages/MarketPage';
import { ComingSoonPage } from '@/pages/ComingSoonPage';
import { NewsListPage } from '@/pages/NewsListPage';
import { MarketReportsPage } from '@/pages/MarketReportsPage';
import { MarketReportDetailPage } from '@/pages/MarketReportDetailPage';
import { TickerPage } from '@/pages/TickerPage';
import { ChartPatternsPage } from '@/pages/ChartPatternsPage';
import { TagPage } from '@/pages/TagPage';
import { TagsIndexPage } from '@/pages/TagsIndexPage';
import { NotificationsPage } from '@/pages/NotificationsPage';
import { ExchangePage } from '@/pages/ExchangePage';
import { ExchangeHistoryPage } from '@/pages/ExchangeHistoryPage';
import { PointHistoryPage } from '@/pages/PointHistoryPage';
import { MessagesPage } from '@/pages/MessagesPage';
/* 2026-05-26 /premium 숨김 — 홈 리다이렉트. 복원 시 아래 import + 라우트 주석 해제. */
// import { PremiumPage } from '@/pages/PremiumPage';
import { OnboardingPage } from '@/pages/OnboardingPage';
import { DiaryListPage } from '@/pages/DiaryListPage';
import { DiaryCreatePage } from '@/pages/DiaryCreatePage';
import { DiaryPositionPage } from '@/pages/DiaryPositionPage';
import { PublicDiaryTickerPage } from '@/pages/PublicDiaryTickerPage';
import { UserPublicDiaryPage } from '@/pages/UserPublicDiaryPage';
// [법인 전환 후 활성화] 모의투자 페이지 — 현재 ComingSoonPage 로 대체
// import { VirtualPage } from '@/pages/VirtualPage';
import { RankingPage } from '@/pages/RankingPage';
import { AveragingDownCalculatorPage } from '@/pages/AveragingDownCalculatorPage';
import { FollowingPage } from '@/pages/FollowingPage';
import { SettingsPage } from '@/pages/SettingsPage';
import { SupportPage } from '@/pages/SupportPage';
import { FaqPage } from '@/pages/FaqPage';
import { NewsDetailPage } from '@/pages/NewsDetailPage';
import { UserProfilePage } from '@/pages/UserProfilePage';
import { UserPostsPage } from '@/pages/UserPostsPage';
import { MyCommentsPage } from '@/pages/MyCommentsPage';
import { UserFollowersPage } from '@/pages/UserFollowersPage';
import { UserFollowingPage } from '@/pages/UserFollowingPage';
import { CalendarPage } from '@/pages/CalendarPage';
import { RealEstateStatusPage } from '@/pages/RealEstateStatusPage';
import { RealEstateStatusOtherPage } from '@/pages/RealEstateStatusOtherPage';
import { EventsPage } from '@/pages/EventsPage';
import { EventDetailPage } from '@/pages/EventDetailPage';
import { TermsPage } from '@/pages/TermsPage';
import { PrivacyPage } from '@/pages/PrivacyPage';
import { DisclaimerPage } from '@/pages/DisclaimerPage';
import { AdminDashboardPage } from '@/pages/AdminDashboardPage';
import { DashboardPage as AdminDashboardPageNew } from '@/pages/admin/DashboardPage';
import { UsersListPage as AdminUsersListPage } from '@/pages/admin/UsersListPage';
import { UserDetailPage as AdminUserDetailPage } from '@/pages/admin/UserDetailPage';
import { PostsListPage as AdminPostsListPage } from '@/pages/admin/PostsListPage';
import { DisclosuresPage as AdminDisclosuresPage } from '@/pages/admin/DisclosuresPage';
import { CommentsListPage as AdminCommentsListPage } from '@/pages/admin/CommentsListPage';
import { ReportsPage as AdminReportsListPage } from '@/pages/admin/ReportsPage';
import { KeywordsPage as AdminKeywordsPage } from '@/pages/admin/KeywordsPage';
import { BoardsPage as AdminBoardsPage } from '@/pages/admin/BoardsPage';
import { NoticesPage as AdminNoticesPage } from '@/pages/admin/NoticesPage';
import { NoticeFormPage as AdminNoticeFormPage } from '@/pages/admin/NoticeFormPage';
import { SystemPage as AdminSystemPage } from '@/pages/admin/SystemPage';
import { UtmBuilderPage as AdminUtmBuilderPage } from '@/pages/admin/UtmBuilderPage';
import { TagManagementPage as AdminTagManagementPage } from '@/pages/admin/TagManagementPage';
import { TickerBadgesPage as AdminTickerBadgesPage } from '@/pages/admin/TickerBadgesPage';
import { TermsAdminPage } from '@/pages/admin/TermsAdminPage';
import { TermsFormPage } from '@/pages/admin/TermsFormPage';
import { SiteSettingsPage as AdminSiteSettingsPage } from '@/pages/admin/SiteSettingsPage';
import { ReferralsPage as AdminReferralsPage } from '@/pages/admin/ReferralsPage';
import { InactivityPage as AdminInactivityPage } from '@/pages/admin/InactivityPage';
import { AdminTodosPage } from '@/pages/admin/TodosPage';
import { ApiCredentialsPage as AdminApiCredentialsPage } from '@/pages/admin/ApiCredentialsPage';
import { ExchangeItemsPage as AdminExchangeItemsPage } from '@/pages/admin/ExchangeItemsPage';
import { PointsPolicyPage as AdminPointsPolicyPage } from '@/pages/admin/PointsPolicyPage';
import { NotFoundPage } from '@/pages/errors/NotFoundPage';
import { ForbiddenPage } from '@/pages/errors/ForbiddenPage';
import { ServerErrorPage } from '@/pages/errors/ServerErrorPage';
import { AdminEventsPage } from '@/pages/admin/EventsPage';
import { AdminEventEditPage } from '@/pages/admin/EventEditPage';
import { TestRunnerPage as AdminTestRunnerPage } from '@/pages/admin/TestRunnerPage';
import { TestLoginPage } from '@/pages/TestLoginPage';
import { AdminReportsPage } from '@/pages/AdminReportsPage';
import { AdminExchangesPage } from '@/pages/AdminExchangesPage';
import { PremiumSuccessPage } from '@/pages/PremiumSuccessPage';
import { PremiumFailPage } from '@/pages/PremiumFailPage';
import { ProfileEditPage } from '@/pages/ProfileEditPage';
import { useUIStore } from '@/store/uiStore';
import { useAuthStore } from '@/store/authStore';
import { storage } from '@/lib/storage';
import { get } from '@/api/client';

/**
 * React Router v7 + basename 조합에서
 * navigate('/') 직후 브라우저 주소창이 basename (끝 슬래시 없음)으로 굳어지는 현상을 방어.
 * 매 네비게이션마다 경로가 정확히 basename 이면 BASE_URL(끝 슬래시 포함)로 정규화.
 * (Vite BASE_URL: prod=/, stage=/sisegonggan-dev/)
 */
function LocationNormalizer() {
  const location = useLocation();
  useEffect(() => {
    const baseUrl = import.meta.env.BASE_URL; // '/' 또는 '/sisegonggan-dev/'
    const basename = baseUrl.replace(/\/$/, '');
    if (window.location.pathname === basename) {
      const normalized = baseUrl + window.location.search + window.location.hash;
      window.history.replaceState(window.history.state, '', normalized);
    }
  }, [location.pathname]);
  return null;
}

/**
 * 친구 추천 코드 캡처 — URL ?ref=XXX 진입 시 7일 쿠키 저장.
 * 가입 직후 AuthCallbackPage 가 쿠키에서 읽어 POST body 에 동봉 → 백엔드 보상 처리.
 */
function ReferralCapture() {
  const location = useLocation();
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const ref = params.get('ref');
    if (!ref) return;
    const cleaned = ref.trim().toUpperCase();
    if (!/^[A-Z0-9]{8}$/.test(cleaned)) return;
    // 동적 import 로 SSR/번들 영향 최소화
    void import('@/lib/cookie').then(({ setCookie }) => setCookie('sg_ref', cleaned, 7));
  }, [location.search]);
  return null;
}

/**
 * 라우트 경로 변경 시 스크롤 최상단으로 이동.
 * - hash(#anchor) 있는 경우는 브라우저 기본 스크롤 위임
 * - DefaultLayout 의 OverlayScrollbars 가 body 에 적용돼 있어 window.scrollTo + body.scrollTop 둘 다 처리
 */
function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) return;
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    if (document.scrollingElement) document.scrollingElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [pathname, hash]);
  return null;
}

/**
 * 앱 부팅 시 authStore.me 를 서버 /users/me 로 강제 새로고침.
 * - localStorage 의 persist 데이터가 낡은 소셜 이미지 URL 등 stale 정보를 들고 있을 수 있음
 * - 서버 응답이 진실 (DB 에 저장된 profile_image_url)
 */
function AuthRefresher() {
  const setMe = useAuthStore((s) => s.setMe);
  const logout = useAuthStore((s) => s.logout);
  const setAuthReady = useAuthStore((s) => s.setAuthReady);

  useEffect(() => {
    const token = storage.get<string>('accessToken');
    if (!token) {
      // 토큰 없음 = 비로그인 확정 → 즉시 ready
      setAuthReady(true);
      return;
    }
    (async () => {
      try {
        const fresh = await get<{
          user_id: string;
          nickname: string;
          level?: number;
          score?: number;
          is_premium?: boolean;
          admin_role?: string | null;
          profile_image_url?: string | null;
          onboarding_done?: boolean;
        }>('/users/me');
        setMe({
          userId: fresh.user_id,
          nickname: fresh.nickname,
          level: fresh.level ?? 1,
          score: fresh.score ?? 0,
          isPremium: fresh.is_premium ?? false,
          adminRole: fresh.admin_role ?? null,
          profileImageUrl: fresh.profile_image_url ?? null,
          onboardingDone: fresh.onboarding_done ?? false,
        });
      } catch {
        // 401 은 인터셉터가 refresh 시도 → 실패 시 이미 logout 호출됨. 그 외 네트워크 오류는 유지
        if (!storage.get('accessToken')) logout();
      } finally {
        // 성공/실패 상관없이 첫 평가 완료 → 라우트 가드 진행 가능
        setAuthReady(true);
      }
    })();
  }, [setMe, logout, setAuthReady]);

  return null;
}

/**
 * 모바일 백그라운드 장시간 후 복귀 시 빈화면/stale UI 자동 복구.
 *  - 탭이 hidden → visible 로 바뀔 때 마지막 visible 시각과의 차이를 검사
 *  - 30분 이상 경과하면 window.location.reload() 로 최신 번들 + SW 강제 적용
 *  - 짧은 백그라운드(앱 전환 30분 이내)는 reload 하지 않아 입력 중인 상태 보존
 *
 *  메모리에서 청크가 언로드된 후 동적 import 가 실패하는 케이스는 main.tsx 의
 *  글로벌 error/unhandledrejection 핸들러가 별도 복구함. */
const STALE_RELOAD_MS = 30 * 60 * 1000;
function VisibilityReloader() {
  const lastVisibleAtRef = useRef<number>(Date.now());
  useEffect(() => {
    const onVisibility = () => {
      if (document.hidden) {
        lastVisibleAtRef.current = Date.now();
        return;
      }
      const elapsed = Date.now() - lastVisibleAtRef.current;
      if (elapsed >= STALE_RELOAD_MS) {
        window.location.reload();
      } else {
        lastVisibleAtRef.current = Date.now();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);
  return null;
}

export default function App() {
  const { theme } = useUIStore();

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  return (
    <ErrorBoundary>
      <LocationNormalizer />
      <ReferralCapture />
      <ScrollToTop />
      <AuthRefresher />
      <VisibilityReloader />
      <Routes>
        {/* 온보딩/콜백 (레이아웃 없음) */}
        <Route path="/auth/callback/:provider" element={<AuthCallbackPage />} />
        <Route path="/test-login" element={<TestLoginPage />} />
        <Route path="/onboarding" element={<AuthGuard><OnboardingPage /></AuthGuard>} />

        {/* 기본 레이아웃 */}
        <Route element={<DefaultLayout />}>
          {/* 공개 */}
          <Route path="/" element={<HomePage />} />
          <Route path="/board/popular" element={<PopularBoardPage />} />
          <Route path="/board/:slug" element={<BoardPage />} />
          <Route path="/post/:id" element={<PostDetailPage />} />
          <Route path="/news" element={<NewsListPage />} />
          <Route path="/news/:id" element={<NewsDetailPage />} />
          {/* [법인 전환 후 활성화] <Route path="/market" element={<MarketPage />} /> */}
          {/* /market — 시장상황 목록 / /market/:id — 차트 포함 상세 */}
          <Route path="/market" element={<MarketReportsPage />} />
          <Route path="/market/:id" element={<MarketReportDetailPage />} />
          <Route path="/ticker/:code" element={<TickerPage />} />
          <Route path="/chart-patterns" element={<ChartPatternsPage />} />
          <Route path="/tags" element={<TagsIndexPage />} />
          <Route path="/tag/:slug" element={<TagPage />} />
          <Route path="/search" element={<SearchPage />} />
          <Route path="/ranking" element={<RankingPage />} />
          <Route path="/calculator/averaging-down" element={<AveragingDownCalculatorPage />} />
          <Route path="/calendar" element={<CalendarPage />} />
          <Route path="/realestate-status" element={<RealEstateStatusPage />} />
          <Route path="/realestate-status-other" element={<RealEstateStatusOtherPage />} />
          {/* /premium 숨김 (2026-05-26) — 홈 리다이렉트. 복원: <Route path="/premium" element={<PremiumPage />} /> */}
          <Route path="/premium" element={<Navigate to="/" replace />} />
          <Route path="/premium/success" element={<AuthGuard><PremiumSuccessPage /></AuthGuard>} />
          <Route path="/premium/fail" element={<AuthGuard><PremiumFailPage /></AuthGuard>} />
          <Route path="/events" element={<EventsPage />} />
          <Route path="/events/:id" element={<EventDetailPage />} />
          <Route path="/faq" element={<FaqPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/disclaimer" element={<DisclaimerPage />} />
          <Route path="/users/:userId" element={<UserProfilePage />} />
          <Route path="/users/:userId/posts" element={<UserPostsPage />} />
          <Route path="/users/:userId/followers" element={<UserFollowersPage />} />
          <Route path="/users/:userId/following" element={<UserFollowingPage />} />

          {/* 로그인 필요 */}
          <Route path="/write" element={<AuthGuard><WritePage /></AuthGuard>} />
          <Route path="/me" element={<AuthGuard><MyPage /></AuthGuard>} />
          <Route path="/me/edit" element={<AuthGuard><ProfileEditPage /></AuthGuard>} />
          <Route path="/profile/me/comments" element={<AuthGuard><MyCommentsPage /></AuthGuard>} />
          <Route path="/notifications" element={<AuthGuard><NotificationsPage /></AuthGuard>} />
          <Route path="/messages" element={<AuthGuard><MessagesPage /></AuthGuard>} />
          <Route path="/exchange" element={<AuthGuard><ExchangePage /></AuthGuard>} />
          <Route path="/exchange/history" element={<AuthGuard><ExchangeHistoryPage /></AuthGuard>} />
          <Route path="/points/history" element={<AuthGuard><PointHistoryPage /></AuthGuard>} />
          <Route path="/diary" element={<AuthGuard><DiaryListPage /></AuthGuard>} />
          <Route path="/diary/new" element={<AuthGuard><DiaryCreatePage /></AuthGuard>} />
          <Route path="/diary/ticker/:ticker" element={<AuthGuard><DiaryPositionPage /></AuthGuard>} />
          <Route path="/diary/public/:ticker" element={<PublicDiaryTickerPage />} />
          <Route path="/users/:userId/diary" element={<UserPublicDiaryPage />} />
          {/* [법인 전환 후 활성화] <Route path="/virtual" element={<VirtualPage />} /> */}
          <Route
            path="/virtual"
            element={
              <ComingSoonPage
                title="모의투자 준비중"
                description={'법인 전환 후 다시 제공될 예정이에요.\n조금만 기다려 주세요.'}
              />
            }
          />
          <Route path="/follow" element={<AuthGuard><FollowingPage /></AuthGuard>} />
          <Route path="/settings" element={<AuthGuard><SettingsPage /></AuthGuard>} />
          <Route path="/support" element={<AuthGuard><SupportPage /></AuthGuard>} />

        </Route>

        {/* 어드민 — DefaultLayout 미사용, RequireAdmin + AdminLayout 중첩 */}
        <Route element={<RequireAdmin />}>
          <Route element={<AdminLayout />}>
            <Route path="/admin" element={<AdminDashboardPageNew />} />
            <Route path="/admin/legacy" element={<AdminDashboardPage />} />
            <Route path="/admin/users" element={<AdminUsersListPage />} />
            <Route path="/admin/users/:id" element={<AdminUserDetailPage />} />
            <Route path="/admin/posts" element={<AdminPostsListPage />} />
            <Route path="/admin/disclosures" element={<AdminDisclosuresPage />} />
            <Route path="/admin/comments" element={<AdminCommentsListPage />} />
            <Route path="/admin/reports" element={<AdminReportsListPage />} />
            <Route path="/admin/reports/legacy" element={<AdminReportsPage />} />
            <Route path="/admin/inactivity" element={<AdminInactivityPage />} />
            <Route path="/admin/todos" element={<AdminTodosPage />} />
            <Route element={<RequireAdmin minRole="content" />}>
              <Route path="/admin/keywords" element={<AdminKeywordsPage />} />
              <Route path="/admin/notices" element={<AdminNoticesPage />} />
              <Route path="/admin/notices/new" element={<AdminNoticeFormPage />} />
              <Route path="/admin/notices/:id/edit" element={<AdminNoticeFormPage />} />
              <Route path="/admin/terms" element={<TermsAdminPage />} />
              <Route path="/admin/terms/new" element={<TermsFormPage />} />
              <Route path="/admin/terms/:id/edit" element={<TermsFormPage />} />
            </Route>
            <Route element={<RequireAdmin minRole="content" />}>
              <Route path="/admin/exchanges" element={<AdminExchangesPage />} />
              <Route path="/admin/exchange-items" element={<AdminExchangeItemsPage />} />
              <Route path="/admin/ticker-badges" element={<AdminTickerBadgesPage />} />
              <Route path="/admin/points-policy" element={<AdminPointsPolicyPage />} />
              <Route path="/admin/events" element={<AdminEventsPage />} />
              <Route path="/admin/events/new" element={<AdminEventEditPage />} />
              <Route path="/admin/events/:id/edit" element={<AdminEventEditPage />} />
            </Route>
            <Route element={<RequireAdmin minRole="super" />}>
              <Route path="/admin/boards" element={<AdminBoardsPage />} />
              <Route path="/admin/system" element={<AdminSystemPage />} />
              <Route path="/admin/utm-builder" element={<AdminUtmBuilderPage />} />
              <Route path="/admin/tags" element={<AdminTagManagementPage />} />
              <Route path="/admin/site-settings" element={<AdminSiteSettingsPage />} />
              <Route path="/admin/referrals" element={<AdminReferralsPage />} />
              <Route path="/admin/api-credentials" element={<AdminApiCredentialsPage />} />
              <Route path="/admin/test-runner" element={<AdminTestRunnerPage />} />
            </Route>
          </Route>
        </Route>

        {/* 명시적 에러 페이지 라우트 — 외부에서 직접 안내 링크로 사용 가능 */}
        <Route path="/403" element={<ForbiddenPage />} />
        <Route path="/500" element={<ServerErrorPage />} />
        {/* catch-all 404 — 위 라우트 매칭 실패 시 폴백 */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      <LoginModal />
      <ConfirmModal />
      <Toaster />
    </ErrorBoundary>
  );
}
